/**
 * The invitation form (Feature: user-and-data-management; **Requirement 6**; Requirements 5.2, 5.6).
 *
 * Structured like {@link import('./AlertRuleForm').AlertRuleForm}: the form owns its draft, the page
 * owns {@link import('../lib/use-write').useWrite}, and validation stays with the API so there is one
 * validator rather than two that disagree. Blank-email and malformed-email rejections therefore arrive
 * as `400 validation_failed` field errors and land beside the input through {@link FormField}.
 *
 * The form's own rule — the one it does enforce locally — is Requirement 6.6: the send is unavailable
 * until the disclosure is acknowledged. Two things make that hold rather than merely appear to:
 *
 *  - **The submit is `disabled` until the box is ticked**, and `handleSubmit` re-checks the same
 *    condition, so a form submitted by pressing Enter in the email field cannot bypass it either.
 *  - **Changing the role clears the acknowledgement.** This is the requirement's real failure mode:
 *    acknowledge the viewer disclosure, then switch to admin, and a single further click would send an
 *    invitation whose write and secret-exfiltration warnings (6.2, 6.5) were never displayed, let
 *    alone acknowledged. The reset makes the acknowledgement specific to the disclosure that was
 *    actually on screen.
 *
 * The role select defaults to `viewer` — the least-privileged option, so the more consequential
 * choices are deliberate.
 */

import { useState, type FormEvent } from 'react';

import type { FieldError } from '@soroush/core';

import type { CreateInvitationPayload, MemberRole } from '../api/types';
import { MEMBER_ROLES } from '../api/types';

import { FormErrorPanel, FormField, unmappedFieldErrors } from './FieldErrorText';
import { ROLE_LABELS, SharingDisclosure } from './SharingDisclosure';

/** The error paths this form renders beside an input; anything else goes to the form-level panel. */
const MAPPED_PATHS = ['invitation.email', 'invitation.role'];

export interface InviteMemberFormProps {
  submitting?: boolean;
  fieldErrors?: readonly FieldError[];
  errorMessage?: string;
  onSubmit: (payload: CreateInvitationPayload) => void;
}

export function InviteMemberForm({
  submitting = false,
  fieldErrors,
  errorMessage,
  onSubmit,
}: InviteMemberFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('viewer');
  const [acknowledged, setAcknowledged] = useState(false);

  // Req 6.6: the disclosure that was acknowledged must be the disclosure that applies to the role
  // being sent, so a role change invalidates a prior acknowledgement.
  function changeRole(next: MemberRole): void {
    setRole(next);
    setAcknowledged(false);
  }

  const canSend = acknowledged && !submitting;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Re-checked here, not only on the button, so an Enter keypress cannot outflank the gate.
    if (!canSend) return;
    onSubmit({ email: email.trim(), role });
  }

  return (
    <form className="entity-form" onSubmit={handleSubmit} noValidate>
      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, MAPPED_PATHS)}
      />

      <FormField
        label="Email address"
        path="invitation.email"
        errors={fieldErrors}
        hint="The invitation grants nothing until this address accepts it."
        render={(field) => (
          <input
            {...field}
            type="email"
            value={email}
            disabled={submitting}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      />

      <FormField
        label="Role"
        path="invitation.role"
        errors={fieldErrors}
        render={(field) => (
          <select
            {...field}
            value={role}
            disabled={submitting}
            onChange={(event) => changeRole(event.target.value as MemberRole)}
          >
            {MEMBER_ROLES.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABELS[option]}
              </option>
            ))}
          </select>
        )}
      />

      <SharingDisclosure
        role={role}
        acknowledgement={{ checked: acknowledged, onChange: setAcknowledged }}
      />

      <div className="form-actions">
        <button type="submit" disabled={!canSend}>
          Send invitation
        </button>
        {!acknowledged ? (
          <p className="muted" data-testid="acknowledgement-required">
            Acknowledge the disclosure above to send this invitation.
          </p>
        ) : null}
      </div>
    </form>
  );
}
